/**
 * CreditEngineAccuracy — /admin/credit-engine/accuracy
 *
 * Wave 5-Late Credit Engine propagation — anchor pattern applied.
 * `scoreColor(v, unit)` now returns a CSS var color string using the
 * BYTE-EXACT thresholds (95 / 80 for %); pastel Tailwind classes
 * removed. Hero KPI cards → anchor `Stat`. Filter chips → sharp
 * SegmentedFilter with accent-soft active state. Source comparison
 * table → hairline Section. Recompute primary → AccentButton with
 * `--ds-color-accent`.
 *
 * TimeSeriesChart palette REWIRED — the four metric colors that were
 * previously pastel hex (`#B794F4` / `#86EFAC` / `#FDE68A` /
 * `#F9A8D4`) are now:
 *   • parser accuracy       → `--ds-color-accent-strong`
 *   • gate PASS rate        → `--ds-color-success-muted`
 *   • AI Credit Analyst cost→ `--ds-color-warning`
 *   • Letter generation cost→ `--ds-color-info`
 * Delta arrows drop the `#86EFAC` / `#FCA5A5` pair for
 * `--ds-color-success-muted` / `--ds-color-danger`.
 *
 * Business logic (parallel window/source fetches → source comparison
 * table + 4 sparklines + aggregate sidebar) preserved BYTE-EXACT.
 * All threshold values (`>=95` / `>=80`) unchanged. Sacred-rule
 * copy in AdminPageHelp preserved. All testids unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, PlayCircle, Info, Filter, BarChart3, Layers } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { AccentButton, GhostButton, Section, SegmentedFilter } from "@/admin/_primitives";

const WINDOWS = ["7d", "30d", "90d"];
const SOURCES = ["all", "credit_hero", "ocr", "idiq"];

const SOURCE_LABELS = {
  all: "All sources",
  credit_hero: "Credit Hero",
  ocr: "OCR",
  idiq: "IDIQ",
};

const TIMESERIES_METRICS = [
  { id: "parser_accuracy_pct",              label: "Parser accuracy",         color: "var(--ds-color-accent-strong)", unit: "%" },
  { id: "confidence_gate_pass_rate_pct",    label: "Gate PASS rate",          color: "var(--ds-color-success-muted)", unit: "%" },
  { id: "analyst_cost_usd_per_report",      label: "AI Credit Analyst cost",  color: "var(--ds-color-warning)",       unit: "$" },
  { id: "letter_cost_usd_per_report",       label: "Letter generation cost",  color: "var(--ds-color-info)",          unit: "$" },
];

const SOURCE_TABLE_METRICS = [
  "parser_accuracy_pct",
  "ocr_accuracy_pct",
  "negative_detection_pct",
  "bureau_preservation_pct",
  "confidence_gate_pass_rate_pct",
  "confidence_gate_fail_closed_rate_pct",
  "ocr_fallback_utilization_pct",
  "review_queue_entry_rate_per_day",
  "analyst_cost_usd_per_report",
  "letter_cost_usd_per_report",
];

function fmtValue(v, unit) {
  if (v == null) return "—";
  if (unit === "%") return `${Number(v).toFixed(1)}%`;
  if (unit === "$" || unit === "USD") return `$${Number(v).toFixed(4)}`;
  if (unit === "ms") return `${Math.round(Number(v))} ms`;
  if (unit === "entries/day") return `${Number(v).toFixed(2)}/day`;
  if (unit === "count") return String(v);
  return String(v);
}

/* Threshold values 95 / 80 for pct unit — BYTE-EXACT; only color strings rewired. */
function scoreColor(v, unit) {
  if (v == null) return "var(--ds-color-ink-muted)";
  if (unit === "%") {
    if (v >= 95) return "var(--ds-color-success-muted)";
    if (v >= 80) return "var(--ds-color-warning)";
    return "var(--ds-color-danger)";
  }
  return "var(--ds-color-ink)";
}

/* Local admin primitives (mirror anchor). */




/* Hero KPI card — anchor `Stat` shape with a sub caption for cost/sample metadata. */
function HeroCard({ testid, label, value, unit, sub }) {
  return (
    <div
      data-testid={testid}
      className="rounded-[var(--ds-radius-card)] p-3"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-admin-border)",
        boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
      }}
    >
      <div
        className="text-[10px] uppercase tracking-[0.16em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div
        className="mt-1.5 text-[26px] leading-tight tabular-nums font-semibold"
        style={{ color: scoreColor(value, unit) }}
      >
        {value == null ? "—" : fmtValue(value, unit)}
      </div>
      {sub && (
        <div className="text-[10px] mt-1 tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function CreditEngineAccuracy() {
  const [window, setWindow] = useState("30d");
  const [source, setSource] = useState("all");
  const [snapshot, setSnapshot] = useState(null);
  const [comparison, setComparison] = useState({});
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [primary, ...rest] = await Promise.all([
        api.get(`/admin/credit-engine/accuracy?window=${window}&source=${source}`),
        ...SOURCES.filter((s) => s !== source).map((s) =>
          api.get(`/admin/credit-engine/accuracy?window=${window}&source=${s}`),
        ),
      ]);
      setSnapshot(primary.data);
      const cmp = { [source]: primary.data };
      const remaining = SOURCES.filter((s) => s !== source);
      remaining.forEach((s, idx) => {
        cmp[s] = rest[idx]?.data;
      });
      setComparison(cmp);
      const serPromises = TIMESERIES_METRICS.map((m) =>
        api
          .get(`/admin/credit-engine/accuracy/time-series?metric=${m.id}&window=${window}&source=${source}`)
          .then((r) => [m.id, r.data.series || []])
          .catch(() => [m.id, []]),
      );
      const pairs = await Promise.all(serPromises);
      setSeries(Object.fromEntries(pairs));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [window, source]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function recompute() {
    setRecomputing(true);
    try {
      const res = await api.post("/admin/credit-engine/accuracy/recompute");
      toast.success(
        `Wrote ${res.data.metric_datapoints_written} datapoints across ${res.data.sources_processed} sources.`,
      );
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRecomputing(false);
    }
  }

  const metrics = snapshot?.metrics || {};
  const catalogue = snapshot?.catalogue || {};
  const agg = snapshot?.aggregate_stats || {};

  return (
    <AdminLayout
      title="Credit accuracy"
      subtitle="Deterministic quality + gate metrics across the credit engine"
      actions={
        <div className="flex gap-2">
          <GhostButton onClick={reload} data-testid="accuracy-reload-btn" className="!py-1 !text-[11px]">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
          <AccentButton onClick={recompute} disabled={recomputing} data-testid="accuracy-recompute-btn">
            <PlayCircle className="w-3 h-3" />
            {recomputing ? "Recomputing…" : "Recompute"}
          </AccentButton>
        </div>
      }
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Credit accuracy dashboard"
          purpose="Consolidates 16 credit-engine health metrics into a single operator view. Segments by source (Credit Hero / OCR / IDIQ) and time window (7 / 30 / 90 days). No AI in metric computation — every value is a deterministic query over persisted data."
          configures="Read-only diagnostic + on-demand recompute trigger."
          impact="No user-facing impact."
          safety="Read-only. No PII on the dashboard. Cost telemetry uses stored token counts × standard per-1M-token rates."
        />
      </div>

      <Section title="Filter" icon={Filter} testid="accuracy-filters">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div
              className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Window
            </div>
            <SegmentedFilter
              options={WINDOWS.map((w) => ({ value: w, label: w }))}
              value={window}
              onChange={setWindow}
              testIdPrefix="accuracy-window"
            />
          </div>
          <div>
            <div
              className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Source
            </div>
            <SegmentedFilter
              options={SOURCES.map((s) => ({ value: s, label: SOURCE_LABELS[s] }))}
              value={source}
              onChange={setSource}
              testIdPrefix="accuracy-source"
            />
          </div>
          {loading && (
            <div
              className="text-[11px] ml-auto"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              Loading…
            </div>
          )}
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-3">
        <div className="space-y-3">
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
            data-testid="accuracy-hero-cards"
          >
            <HeroCard
              testid="accuracy-hero-parser"
              label="Parser accuracy"
              value={metrics.parser_accuracy_pct?.value}
              unit={metrics.parser_accuracy_pct?.unit}
              sub={`Corpus run ${(metrics.parser_accuracy_pct?.corpus_run_id || "").slice(-13) || "—"}`}
            />
            <HeroCard
              testid="accuracy-hero-gate"
              label="Gate PASS rate"
              value={metrics.confidence_gate_pass_rate_pct?.value}
              unit={metrics.confidence_gate_pass_rate_pct?.unit}
              sub={`sample ${metrics.confidence_gate_pass_rate_pct?.sample_size || 0} reports`}
            />
            <HeroCard
              testid="accuracy-hero-analyst-cost"
              label="AI Credit Analyst cost"
              value={metrics.analyst_cost_usd_per_report?.value}
              unit={metrics.analyst_cost_usd_per_report?.unit}
              sub={`total $${(metrics.analyst_cost_usd_per_report?.total_cost_usd || 0).toFixed(4)} · ${
                metrics.analyst_cost_usd_per_report?.sample_size || 0
              } explanations`}
            />
            <HeroCard
              testid="accuracy-hero-letter-cost"
              label="Letter generation cost"
              value={metrics.letter_cost_usd_per_report?.value}
              unit={metrics.letter_cost_usd_per_report?.unit}
              sub={`total $${(metrics.letter_cost_usd_per_report?.total_cost_usd || 0).toFixed(4)} · ${
                metrics.letter_cost_usd_per_report?.sample_size || 0
              } drafts`}
            />
            <HeroCard
              testid="accuracy-hero-time"
              label="Avg processing time"
              value={metrics.avg_processing_time_ms?.value}
              unit={metrics.avg_processing_time_ms?.unit}
              sub={`over ${agg.total_imports_in_window || 0} imports`}
            />
          </div>

          <Section
            title="Source comparison"
            icon={Layers}
            testid="accuracy-source-comparison"
            bodyClassName="p-0"
            badge={
              <span
                className="text-[10px] uppercase tracking-[0.14em] font-semibold flex items-center gap-1"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                <Info className="w-3 h-3" /> IDIQ Available Soon
              </span>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr
                    className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                    style={{
                      color: "var(--ds-color-ink-subtle)",
                      background: "var(--ds-color-admin-surface-muted)",
                      borderBottom: "1px solid var(--ds-color-admin-border)",
                    }}
                  >
                    <th className="px-4 py-2 font-semibold">Metric</th>
                    {SOURCES.map((s) => (
                      <th key={s} className="px-4 py-2 font-semibold text-right">
                        {SOURCE_LABELS[s]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SOURCE_TABLE_METRICS.map((metricId, idx) => {
                    const meta = catalogue[metricId] || {};
                    const isLast = idx === SOURCE_TABLE_METRICS.length - 1;
                    return (
                      <tr
                        key={metricId}
                        data-testid={`accuracy-cmp-row-${metricId}`}
                        style={{
                          borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        }}
                      >
                        <td
                          className="px-4 py-1.5 text-[12px]"
                          style={{ color: "var(--ds-color-ink)" }}
                          title={meta.description}
                        >
                          {meta.label || metricId}
                        </td>
                        {SOURCES.map((s) => {
                          const v = comparison[s]?.metrics?.[metricId];
                          return (
                            <td
                              key={s}
                              className="px-4 py-1.5 text-right tabular-nums font-mono text-[12px]"
                              style={{ color: scoreColor(v?.value, v?.unit) }}
                              data-testid={`accuracy-cmp-cell-${metricId}-${s}`}
                            >
                              {v?.status === "not_available" || v?.status === "not_available_placeholder" ? (
                                <span
                                  className="italic text-[10px]"
                                  style={{ color: "var(--ds-color-ink-muted)" }}
                                >
                                  {v?.status === "not_available_placeholder" ? "Available Soon" : "N/A"}
                                </span>
                              ) : (
                                fmtValue(v?.value, v?.unit)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            title="Time series"
            icon={BarChart3}
            testid="accuracy-timeseries-section"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="accuracy-timeseries-grid">
              {TIMESERIES_METRICS.map((m) => (
                <TimeSeriesChart
                  key={m.id}
                  metric={m}
                  data={series[m.id] || []}
                  testid={`accuracy-ts-${m.id}`}
                />
              ))}
            </div>
          </Section>
        </div>

        <aside className="space-y-3" data-testid="accuracy-agg-sidebar">
          <Section title={`Aggregate stats (${window})`} testid="accuracy-agg-section">
            <div className="space-y-2 text-[13px]">
              <AggRow label="Total imports" value={agg.total_imports_in_window} />
              <AggRow label="Scored (with gate)" value={agg.scored_imports_in_window} />
              <AggRow label="Total findings" value={agg.total_findings_in_window} />
              <AggRow label="Letter drafts created" value={agg.letter_drafts_created_in_window} />
              <AggRow label="Letter drafts finalized" value={agg.letter_drafts_finalized_in_window} />
              <AggRow label="Review-queue entries" value={agg.review_queue_entries_in_window} />
            </div>
          </Section>

          <Section title="Available Soon" testid="accuracy-available-soon">
            <ul
              className="list-disc pl-4 space-y-1 text-[12px]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              <li>Letter acceptance rate — requires user-side letter acceptance UI.</li>
              <li>Dispute success rate — requires bureau-response tracking.</li>
              <li>IDIQ segment — activates on integration go-live.</li>
            </ul>
          </Section>
        </aside>
      </div>
    </AdminLayout>
  );
}

function AggRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-[12px]" style={{ color: "var(--ds-color-ink-muted)" }}>
        {label}
      </span>
      <span className="tabular-nums font-mono" style={{ color: "var(--ds-color-ink)" }}>
        {value ?? 0}
      </span>
    </div>
  );
}

/* SVG sparkline — palette rewired to admin tokens per metric.color. */
function TimeSeriesChart({ metric, data, testid }) {
  const width = 420;
  const height = 90;
  const padX = 6;
  const padY = 8;
  const values = data.filter((d) => d.value != null).map((d) => d.value);
  const anyData = values.length > 0;
  const min = anyData ? Math.min(...values, 0) : 0;
  const max = anyData ? Math.max(...values, 1) : 1;
  const range = max - min || 1;

  const n = data.length;
  const step = n > 1 ? (width - padX * 2) / (n - 1) : 0;
  const y = (v) => padY + (1 - (v - min) / range) * (height - padY * 2);
  const x = (i) => padX + i * step;
  let path = "";
  let started = false;
  data.forEach((d, i) => {
    if (d.value == null) {
      started = false;
      return;
    }
    path += `${started ? "L" : "M"}${x(i).toFixed(2)},${y(d.value).toFixed(2)} `;
    started = true;
  });

  const first = values[0];
  const last = values[values.length - 1];
  const delta = first != null && last != null ? last - first : null;
  const deltaColor =
    delta == null
      ? "var(--ds-color-ink-muted)"
      : delta >= 0
      ? "var(--ds-color-success-muted)"
      : "var(--ds-color-danger)";

  return (
    <div
      className="rounded-[var(--ds-radius-card)] p-3"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-admin-border)",
      }}
      data-testid={testid}
    >
      <div className="flex justify-between items-baseline mb-1.5">
        <div
          className="text-[10px] uppercase tracking-[0.16em] font-semibold"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          {metric.label} · {n}-day trend
        </div>
        <div className="text-[11px] tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
          {delta != null && (
            <span style={{ color: deltaColor }}>
              {delta >= 0 ? "+" : ""}
              {metric.unit === "%" ? `${delta.toFixed(1)}pp` : delta.toFixed(4)}
            </span>
          )}
        </div>
      </div>
      {anyData ? (
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-[80px]">
          <path d={path} stroke={metric.color} strokeWidth="1.75" fill="none" />
          {data.map((d, i) =>
            d.value == null ? null : (
              <circle
                key={i}
                cx={x(i).toFixed(2)}
                cy={y(d.value).toFixed(2)}
                r={i === n - 1 ? 3 : 1.5}
                fill={metric.color}
              >
                <title>{`${d.date}: ${d.value.toFixed(2)}${metric.unit}`}</title>
              </circle>
            ),
          )}
        </svg>
      ) : (
        <div
          className="text-[11px] py-6 text-center"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          No datapoints yet. Recompute or wait for the nightly cron @ 03:10 UTC.
        </div>
      )}
      <div
        className="flex justify-between text-[9px] tabular-nums mt-1"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}
